<script setup>
import { computed } from 'vue';
import { useQuery } from 'villus';
import { useStore } from '@/store/Store.js';
import { GA } from '@/mixins/Ga';
import BioCtaSubnav from './BioCtaSubnav.vue';

const props = defineProps({
    type: {
        type: String,
        default: '',
    },
    data: {
        type: Object,
        default: () => {},
    },
    color: {
        type: String,
        default: '',
    },
    mainColor: {
        type: String,
        default: 'red',
    },
    themeColor: {
        type: String,
        default: '',
    },
    center: {
        type: Boolean,
        default: false,
    },
});

const store = useStore();
const round = (v, n) => Math.round(v / n) * n;

let factor = 0.375;
if (store.md.value) {
    factor = 0.326;
}

const variables = computed(() => ({
    lang: store.lang.value,
    width: store.xs.value ? 800 : parseInt(round(window.innerWidth, 50) * factor, 10) * window.devicePixelRatio,
    height: store.xs.value ? 800 : parseInt(round(window.innerHeight, 50) * window.devicePixelRatio, 10),
}));
const ga = new GA({ category: 'midBody', action: 'clicInterne' });

const { data } = await useQuery({
    variables,
    query: `query FetchEntries($lang: [String], $width: Int, $height: Int) {
        entries (section: "bio", site: $lang, level: 1, type: "principal") {
            uri
             ... on bio_principal_Entry {
                cover: bioCover {
                    title,
                        url @transform (format: "webp", quality: 80, width: $width, height: $height, immediately: true )
                },
                cta: bioCarouselCta,
                title: bioCarouselTitle,
                description: bioCarouselDescription
                sections: children (level: 2, type: "segment") {
                    title
                }
            }
        }
    }`,
});
</script>

<template>
    <div
        ref="el"
        class="BioCta Component relative z-0 mb-0"
    >
        <template v-if="data">
            <div
                v-for="panel in data.entries"
                :key="panel.title"
                class="BioCta__panel group relative flex flex-col md:h-screen md:flex-row md:overflow-hidden"
                :class="['odd:bg-dark-' + mainColor, 'even:bg-light-' + mainColor, 'even:text-dark-' + mainColor]"
            >
                <div
                    class="BioCta__cover lazyload relative inset-y-0 -mt-4 h-95 w-full overflow-hidden rounded-xl bg-cover bg-scroll bg-left bg-no-repeat ios:!bg-scroll md:absolute md:mt-0 md:h-screen md:w-140 xl:bg-h-screen xl:bg-fixed"
                    :style="{
                        'background-image': `url(${panel.cover[0].url})`,
                    }"
                />
                <div
                    class="BioCta__block relative z-10 flex w-full flex-col items-center justify-between px-6 md:w-full md:flex-row md:px-12 xl:ml-33 xl:mr-48 xl:px-0"
                >
                    <div class="BioCta__title -mt-20 text-6xl font-medium text-white md:mt-0 md:w-69 xl:w-106 xl:text-8xl">
                        {{ panel.title }}
                    </div>
                    <div class="BioCta__description mt-12 w-full md:mt-0 md:w-119 xl:w-111">
                        <h5
                            class="mb-8"
                            :class="{ h5: landscape, h4: portrait, h4: tablet }"
                        >
                            {{ panel.description }}
                        </h5>
                        <router-link :to="setUri({ uri: panel.uri })">
                            <span v-on:click="ga.send({ label: 'decouvrir' + panel.title })">
                                <Btn
                                    :class="[
                                        'group-odd:text-dark-' + mainColor,
                                        'group-odd:bg-light-' + mainColor,
                                        'group-even:text-white',
                                        'group-even:bg-dark-' + mainColor,
                                    ]"
                                    :text="panel.cta"
                                />
                            </span>
                        </router-link>
                        <BioCtaSubnav
                            :items="panel.sections"
                            :parent-u-r-i="panel.uri"
                        />
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>
